import React, { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { useAuthStore } from '../../store/authStore'
import { FileText, Plus, Calendar, MapPin, DollarSign, Users, Eye } from 'lucide-react'
import { CreateRfqModal } from '../../components/enterprise/CreateRfqModal'
import { RfqDetailModal } from '../../components/enterprise/RfqDetailModal'
import { apiService } from '../../services/api'

interface EnterpriseRfq {
  id: string
  title: string
  description: string
  deadline: string
  budgetRange?: string
  location?: string
  isOpen: boolean
  createdAt: string
  responses?: EnterpriseRfqResponse[]
}

interface EnterpriseRfqResponse {
  id: string
  responseText: string
  proposedPrice?: number
  deliveryDays?: number
  status: 'PENDING' | 'ACCEPTED' | 'REJECTED'
  vendor: {
    id: string
    firstName: string
    lastName: string
    companyName?: string
  }
  createdAt: string
}

export const EnterpriseRfqPage: React.FC = () => {
  const { user } = useAuthStore()
  const navigate = useNavigate()
  const [activeTab, setActiveTab] = useState<'enterprise' | 'post-quote-activity'>('enterprise')
  const [showCreateModal, setShowCreateModal] = useState(false)
  const [showDetailModal, setShowDetailModal] = useState(false)
  const [selectedRfqId, setSelectedRfqId] = useState<string | null>(null)
  const [rfqs, setRfqs] = useState<EnterpriseRfq[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    if (!user) {
      navigate('/auth/login')
      return
    }

    // Allow BUYER users with isEnterprise=true or ENTERPRISE_BUYER role
    const isEnterpriseBuyer = (user.role === 'BUYER' && user.isEnterprise) || user.role === 'ENTERPRISE_BUYER'
    if (!isEnterpriseBuyer) {
      navigate('/', { replace: true })
      return
    }
  }, [user, navigate])

  useEffect(() => {
    if (activeTab === 'enterprise' && user) {
      fetchEnterpriseRfqs()
    }
  }, [activeTab, user])

  const fetchEnterpriseRfqs = async () => {
    try {
      setLoading(true)
      setError(null)
      const response = await apiService.get<any>('/api/enterprise/rfqs/buyer/rfqs')
      if (response.success) {
        setRfqs(response.data?.rfqs || [])
      } else {
        setError('Failed to fetch RFQs')
      }
    } catch (err) {
      console.error('Error fetching RFQs:', err)
      setError('Failed to fetch RFQs')
    } finally {
      setLoading(false)
    }
  }

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    })
  }

  const getStatusColor = (isOpen: boolean) => {
    return isOpen ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'
  }

  const getResponseCount = (rfq: EnterpriseRfq) => {
    return rfq.responses?.length || 0
  }

  if (!user || ((user.role !== 'BUYER' || !user.isEnterprise) && user.role !== 'ENTERPRISE_BUYER')) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Checking access permissions...</p>
        </div>
      </div>
    )
  }

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm">
        {/* Header */}
        <div className="border-b border-gray-200">
          <div className="px-4 sm:px-6 py-4">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
              <div>
                <h1 className="text-2xl sm:text-3xl font-bold text-gray-900">Request for Quotes</h1>
                <p className="mt-1 text-sm text-gray-500">
                  Manage your RFQs and view vendor responses
                </p>
              </div>
              <button
                onClick={() => setShowCreateModal(true)}
                className="bg-primary-600 hover:bg-primary-700 text-white px-4 py-2 rounded-lg flex items-center justify-center space-x-2 w-full sm:w-auto"
              >
                <Plus className="h-4 w-4 sm:h-5 sm:w-5" />
                <span className="text-sm sm:text-base">Create RFQ</span>
              </button>
            </div>
          </div>

          {/* Tabs */}
          <div className="px-4 sm:px-6">
            <div className="flex space-x-4 sm:space-x-8 overflow-x-auto">
              <button
                onClick={() => setActiveTab('enterprise')}
                className={`py-2 px-1 border-b-2 font-medium text-sm whitespace-nowrap ${
                  activeTab === 'enterprise'
                    ? 'border-primary-500 text-primary-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                Enterprise RFQs
              </button>
              <button
                onClick={() => setActiveTab('post-quote-activity')}
                className={`py-2 px-1 border-b-2 font-medium text-sm whitespace-nowrap ${
                  activeTab === 'post-quote-activity'
                    ? 'border-primary-500 text-primary-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                Post-Quote Activity
              </button>
            </div>
          </div>
        </div>

        {/* Content */}
        <div className="p-6">
          {activeTab === 'enterprise' ? (
            <>
              {loading ? (
                <div className="text-center py-12">
                  <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mx-auto mb-4"></div>
                  <p className="text-gray-600">Loading your RFQs...</p>
                </div>
              ) : error ? (
                <div className="text-center py-12">
                  <div className="text-red-600 mb-4">{error}</div>
                  <button
                    onClick={fetchEnterpriseRfqs}
                    className="bg-primary-600 hover:bg-primary-700 text-white px-4 py-2 rounded-lg"
                  >
                    Retry
                  </button>
                </div>
              ) : rfqs.length === 0 ? (
                <div className="text-center py-12">
                  <FileText className="h-16 w-16 text-gray-400 mx-auto mb-4" />
                  <h3 className="text-lg font-medium text-gray-900 mb-2">
                    No RFQs Created Yet
                  </h3>
                  <p className="text-gray-600 mb-6">
                    Create your first RFQ to start receiving vendor responses.
                  </p>
                  <button
                    onClick={() => setShowCreateModal(true)}
                    className="bg-primary-600 hover:bg-primary-700 text-white px-4 py-2 rounded-lg"
                  >
                    Create Your First RFQ
                  </button>
                </div>
              ) : (
                <div className="space-y-6">
                  <div className="flex items-center justify-between">
                    <h2 className="text-xl font-semibold text-gray-900">
                      Your RFQs ({rfqs.length})
                    </h2>
                  </div>
                  
                  <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
                    {rfqs.map((rfq) => (
                      <div key={rfq.id} className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-6 hover:shadow-md transition-shadow">
                        <div className="flex items-start justify-between mb-4">
                          <h3 className="text-lg font-semibold text-gray-900 line-clamp-2">
                            {rfq.title}
                          </h3>
                          <span className={`px-2 py-1 text-xs font-medium rounded-full ${getStatusColor(rfq.isOpen)}`}>
                            {rfq.isOpen ? 'Open' : 'Closed'}
                          </span>
                        </div>
                        
                        <p className="text-gray-600 text-sm mb-4 line-clamp-3">
                          {rfq.description}
                        </p>
                        
                        <div className="space-y-2 mb-4">
                          {rfq.budgetRange && (
                            <div className="flex items-center text-sm text-gray-500">
                              <DollarSign className="h-4 w-4 mr-2" />
                              <span>{rfq.budgetRange}</span>
                            </div>
                          )}
                          {rfq.location && (
                            <div className="flex items-center text-sm text-gray-500">
                              <MapPin className="h-4 w-4 mr-2" />
                              <span>{rfq.location}</span>
                            </div>
                          )}
                          <div className="flex items-center text-sm text-gray-500">
                            <Calendar className="h-4 w-4 mr-2" />
                            <span>Deadline: {formatDate(rfq.deadline)}</span>
                          </div>
                          <div className="flex items-center text-sm text-gray-500">
                            <Users className="h-4 w-4 mr-2" />
                            <span>{getResponseCount(rfq)} responses</span>
                          </div>
                        </div>
                        
                        <div className="flex items-center justify-between pt-4 border-t border-gray-100">
                          <span className="text-xs text-gray-400">
                            Created {formatDate(rfq.createdAt)}
                          </span>
                          <button
                            onClick={() => {
                              setSelectedRfqId(rfq.id)
                              setShowDetailModal(true)
                            }}
                            className="flex items-center text-sm text-primary-600 hover:text-primary-700"
                          >
                            <Eye className="h-4 w-4 mr-1" />
                            View Details
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </>
          ) : activeTab === 'post-quote-activity' ? (
            <div className="space-y-6">
              <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-4">
                <h2 className="text-xl sm:text-2xl font-semibold text-gray-900">Post-Quote Activity Manager</h2>
                <button
                  onClick={() => navigate('/enterprise/dashboard')}
                  className="bg-primary-600 hover:bg-primary-700 text-white px-4 py-2 rounded-lg text-sm font-medium w-full sm:w-auto"
                >
                  View Full Dashboard
                </button>
              </div>

              <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-6">
                <p className="text-gray-600 mb-4">
                  Manage your post-quote activities including purchase orders, agreements, fulfillment tracking, invoicing, and vendor feedback.
                </p>

                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                    <div className="flex items-center space-x-3">
                      <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center flex-shrink-0">
                        <FileText className="w-5 h-5 text-blue-600" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <h3 className="font-medium text-blue-900 text-sm sm:text-base">Purchase Orders</h3>
                        <p className="text-xs sm:text-sm text-blue-700">Manage POs from accepted RFQs</p>
                      </div>
                    </div>
                  </div>

                  <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                    <div className="flex items-center space-x-3">
                      <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center flex-shrink-0">
                        <FileText className="w-5 h-5 text-green-600" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <h3 className="font-medium text-green-900 text-sm sm:text-base">Digital Agreements</h3>
                        <p className="text-xs sm:text-sm text-green-700">Contracts and NDAs</p>
                      </div>
                    </div>
                  </div>

                  <div className="bg-purple-50 border border-purple-200 rounded-lg p-4">
                    <div className="flex items-center space-x-3">
                      <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center flex-shrink-0">
                        <FileText className="w-5 h-5 text-purple-600" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <h3 className="font-medium text-purple-900 text-sm sm:text-base">Fulfillment Tracking</h3>
                        <p className="text-xs sm:text-sm text-purple-700">Track delivery progress</p>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="mt-6 text-center">
                  <button
                    onClick={() => navigate('/enterprise/post-quote-activity')}
                    className="text-primary-600 hover:text-primary-700 font-medium"
                  >
                    Access Full Post-Quote Activity Manager →
                  </button>
                </div>
              </div>
            </div>
          ) : null}
        </div>
      </div>

      {/* Create RFQ Modal */}
      <CreateRfqModal
        isOpen={showCreateModal}
        onClose={() => setShowCreateModal(false)}
        onSuccess={() => {
          setShowCreateModal(false)
          fetchEnterpriseRfqs()
        }}
      />

      {/* RFQ Detail Modal */}
      {selectedRfqId && (
        <RfqDetailModal
          isOpen={showDetailModal}
          onClose={() => {
            setShowDetailModal(false)
            setSelectedRfqId(null)
          }}
          rfqId={selectedRfqId}
          onSuccess={() => {
            fetchEnterpriseRfqs()
          }}
        />
      )}
    </div>
  )
} 